import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatCentsToDollarString } from '../../utils/formatters/moneyDisplay';
import { get } from '../../../utils/shared/api';
import {
  RowTooltip,
  ListItemWrapper,
  ListContainer,
  InnerListRow,
  InnerListRowWithTooltip,
  SubInnerListRowBold,
  SubInnerListRowItalic
} from './styled/base_components';

const TransactionSummary = ({ appData }) => {
  const { brandProfileId } = JSON.parse(appData);
  const [data, setData] = useState();

  const getData = () => {
    get(`/api/v1/brand_profiles/${brandProfileId}/balance`, false).then(response =>
      setData(response)
    );
  };

  useEffect(() => {
    getData();
  }, []);

  if (!data) return null;

  const contentBalance = formatCentsToDollarString(data.content);
  const editorBalance = formatCentsToDollarString(data.groups.Editor.balance_in_cents);
  const editorFlexBalance = formatCentsToDollarString(data.groups['Editor Flex'].balance_in_cents);
  const strategyBalance = formatCentsToDollarString(data.groups.Strategy.balance_in_cents);
  const distributionBalance = formatCentsToDollarString(data.groups.Distribution.balance_in_cents);
  const availableBalance = formatCentsToDollarString(data.available);
  const pendingTransactions = formatCentsToDollarString(data.pending);

  // FARR (Fixed ARR) data
  const hasFarr = data.farr && data.farr.usable;
  const farrBalance = hasFarr ? formatCentsToDollarString(data.farr.balance) : null;
  const farrDaysRemaining = hasFarr ? data.farr.days_remaining : null;
  const farrExpirationDate = hasFarr ? data.farr.term_end_date : null;
  const isExpiringWarning = hasFarr && farrDaysRemaining !== null && farrDaysRemaining <= 30;
  const regularBalance = data.regular_balance !== undefined 
    ? formatCentsToDollarString(data.regular_balance) 
    : availableBalance;
  const combinedAvailable = data.combined_available !== undefined 
    ? formatCentsToDollarString(data.combined_available) 
    : availableBalance;

  const tooltipPendingPayments = (
    <p style={{ fontStyle: 'normal' }}>
      The total upcoming payments on stories that are still in progress
    </p>
  );
  const tooltipAvailableBalance = (
    <p>
      This is the total balance available to spend on content. If you need more funds, please reach
      out to your Contently contact.
    </p>
  );
  const tooltipBankBalance = (
    <div>
      {hasFarr && (
        <p><strong>Fixed ARR:</strong> Pre-paid content credits with a lower margin rate. Used first for content payments.</p>
      )}
      <p>Content: Funds for content production to pay freelancers for their work</p>
      <p>Editor: Funds for Managing Editor services a fixed amount is debited monthly.</p>
      <p>Editor Flex: Funds for various Editor services or monthly overages. Debited monthly.</p>
      <p>Strategy: Funds for Strategy services deliverables. Debited upon delivery.</p>
      <p>Distribution: Funds for Distribution services. Debited monthly.</p>
    </div>
  );
  const tooltipEditorFlex = (
    <p>
      This bank of funds is used to pay for additional ME hours as needed, as well as hours worked
      by your Contently creative managers including: Photo Editors, Art Directors, Multimedia
      Project Managers, and Copy Editors. If you need more funds, please reach out to your Contently
      contact.
    </p>
  );
  const tooltipFarrBalance = (
    <div>
      <p>
        <strong>Fixed ARR Credits:</strong> Pre-paid content funds at a reduced margin rate.
      </p>
      <p>
        These credits are automatically used first when paying for content. Once exhausted, 
        regular content balance will be used.
      </p>
      {farrExpirationDate && (
        <p style={{ color: isExpiringWarning ? '#e67700' : 'inherit' }}>
          Expires: {farrExpirationDate}
        </p>
      )}
    </div>
  );

  // Styles for FARR warning
  const farrWarningStyle = isExpiringWarning ? { color: '#e67700', fontWeight: 'bold' } : {};
  const farrExpirationStyle = isExpiringWarning 
    ? { color: '#e67700', fontSize: '0.85em' } 
    : { color: '#666', fontSize: '0.85em' };

  return (
    <ListContainer>
      <h5>
        Bank Balance
        <RowTooltip tooltip={tooltipBankBalance} />
      </h5>
      <ListItemWrapper>
        {/* FARR Balance Section - Only show if active FARR exists */}
        {hasFarr && (
          <React.Fragment>
            <InnerListRowWithTooltip
              key="FARR"
              leftCol={
                <span style={farrWarningStyle}>
                  Fixed ARR Balance
                  {isExpiringWarning && <span title="Expiring soon"> ⚠️</span>}
                </span>
              }
              rightCol={<span style={farrWarningStyle}>{farrBalance}</span>}
              tooltip={tooltipFarrBalance}
            />
            <SubInnerListRowItalic
              key="FARRExpiry"
              leftCol={
                <span style={farrExpirationStyle}>
                  {`Expires: ${farrExpirationDate}`}
                </span>
              }
              rightCol={
                <span style={farrExpirationStyle}>
                  {`${farrDaysRemaining} days remaining`}
                </span>
              }
            />
          </React.Fragment>
        )}

        {/* Regular Content Balance */}
        <InnerListRow 
          key="Content" 
          leftCol={hasFarr ? "Regular Content" : "Content"} 
          rightCol={hasFarr ? regularBalance : contentBalance} 
        />
        <SubInnerListRowItalic
          key="Pending"
          leftCol="Pending Payments"
          rightCol={`-${pendingTransactions}`}
          tooltip={tooltipPendingPayments}
        />
        <SubInnerListRowBold
          key="Balance"
          leftCol={hasFarr ? "Total Available" : "Available Balance"}
          rightCol={hasFarr ? combinedAvailable : availableBalance}
          tooltip={tooltipAvailableBalance}
        />
        
        {/* Other balance types */}
        <InnerListRow key="Editor" leftCol="Editor" rightCol={editorBalance} />
        <InnerListRowWithTooltip
          key="Editor Flex"
          leftCol="Editor Flex"
          rightCol={editorFlexBalance}
          tooltip={tooltipEditorFlex}
        />
        <InnerListRow key="Strategy" leftCol="Strategy" rightCol={strategyBalance} />
        <InnerListRow key="Distribution" leftCol="Distribution" rightCol={distributionBalance} />
      </ListItemWrapper>
    </ListContainer>
  );
};

TransactionSummary.propTypes = {
  appData: PropTypes.string.isRequired
};

export default TransactionSummary;



// WEBPACK FOOTER //
// ./app/javascript/app/components/transactions/transaction_summary.jsx